/**
 * The game host: everything needed to run one scored game inside the shell.
 * Owns the ONE shared canvas + WebGL renderer (§5.2 — created once, reused
 * across games and plays), the loop, input wiring, resize, and the
 * choice-point overlay. Games arrive as lazy modules.
 */

import type { InputFrame, QualityTier, RoundConfig, RunTelemetry } from '@hnplay/shared';
import {
  createGameLoop,
  createPointerKeyboardInput,
  createSharedRenderer,
  detectTier,
  pixelRatioForTier,
  prefersReducedMotion,
  type GameModule,
  type PointerKeyboardInput,
  type ViewContext,
} from '@hnplay/game-sdk';
import type { WebGLRenderer } from 'three';
import { showChoiceOverlay, type ProductInfo } from './choice-overlay.js';

let shared: { canvas: HTMLCanvasElement; renderer: WebGLRenderer; tier: QualityTier } | null = null;

function getShared(): { canvas: HTMLCanvasElement; renderer: WebGLRenderer; tier: QualityTier } {
  if (!shared) {
    const canvas = document.createElement('canvas');
    canvas.className = 'hnp-canvas';
    const tier = detectTier();
    shared = { canvas, renderer: createSharedRenderer(canvas, tier), tier };
  }
  return shared;
}

/** The ONE shared canvas+renderer (§5.2) — also used by the Garden screen. */
export function acquireSharedStage(): { canvas: HTMLCanvasElement; renderer: WebGLRenderer; tier: QualityTier } {
  return getShared();
}

export interface PlayResult {
  score: number;
  frames: InputFrame[];
  telemetry: RunTelemetry;
  /** True when the player quit mid-run: the run is abandoned, never submitted. */
  aborted?: boolean;
}

export async function playGame(opts: {
  module: GameModule<unknown>;
  seed: string;
  config: RoundConfig;
  products: Map<string, ProductInfo>;
  stage: HTMLElement;
  onProgress?: (p: number) => void;
  /** Pre-run gate (§6.4 tilt permission etc.) — runs after mount, holds the
   *  loop until it resolves. Receives the live input for enableOrientation(). */
  gate?: ((input: PointerKeyboardInput, overlay: HTMLElement) => Promise<void>) | undefined;
  /** E2E only (?e2e-instant=1): complete the run headlessly with the scripted
   *  player — a genuine sim + genuine log, just without the 90 real seconds. */
  instant?: boolean;
}): Promise<PlayResult> {
  const { module: gameModule, seed, config, products, stage } = opts;

  if (opts.instant) {
    const { createScriptedInput } = await import('@hnplay/game-sdk');
    const sim = gameModule.createSim();
    await sim.init(seed, config);
    const scripted = createScriptedInput(seed);
    const frames: PlayResult['frames'] = [];
    while (!sim.isOver() && frames.length < 20_000) {
      const frame = scripted.next(sim.getChoicePoint() !== null);
      frames.push(frame);
      sim.step(frame);
      if (frames.length % 900 === 0) await new Promise((r) => setTimeout(r, 0));
    }
    return { score: sim.getScore(), frames, telemetry: sim.getTelemetry() };
  }
  const { canvas, renderer, tier } = getShared();

  const overlay = document.createElement('div');
  overlay.className = 'hnp-overlay';
  stage.appendChild(canvas);
  stage.appendChild(overlay);

  const sim = gameModule.createSim();
  const view = gameModule.createView();
  const ctx: ViewContext = {
    renderer,
    overlay,
    assetBase: '/assets',
    tier,
    reducedMotion: prefersReducedMotion(),
    roundConfig: config,
    products,
  };

  await Promise.all([sim.init(seed, config), view.preload(ctx, opts.onProgress ?? (() => undefined))]);
  view.mount(ctx);

  const applySize = (): void => {
    const w = stage.clientWidth;
    const h = stage.clientHeight;
    if (w === 0 || h === 0) return;
    renderer.setPixelRatio(pixelRatioForTier(tier));
    renderer.setSize(w, h, false);
    view.resize(w, h, pixelRatioForTier(tier));
  };
  applySize();
  const ro = new ResizeObserver(applySize);
  ro.observe(stage);

  const input = createPointerKeyboardInput(overlay, gameModule.keyboardMode ?? 'cursor');
  input.attach();
  if (opts.gate) await opts.gate(input, overlay);

  // Every run needs a way out that isn't the browser back button.
  const quit = document.createElement('button');
  quit.type = 'button';
  quit.className = 'hnp-quit';
  quit.setAttribute('aria-label', 'Quit this run and go back');
  quit.textContent = '‹ Back';
  overlay.appendChild(quit);

  return new Promise<PlayResult>((resolve) => {
    let closeChoice: (() => void) | null = null;
    let done = false;
    const finish = (aborted: boolean): void => {
      if (done) return;
      done = true;
      loop.stop();
      input.detach();
      ro.disconnect();
      closeChoice?.();
      const result: PlayResult = {
        score: sim.getScore(),
        frames: loop.getFrames(),
        telemetry: sim.getTelemetry(),
        ...(aborted ? { aborted: true } : {}),
      };
      view.destroy();
      overlay.remove();
      canvas.remove();
      resolve(result);
    };

    const loop = createGameLoop({
      sim,
      view,
      input,
      hooks: {
        onChoiceOpen(cp): void {
          closeChoice = showChoiceOverlay(overlay, cp, products, (n) => input.selectChoice(n));
        },
        onChoiceClosed(): void {
          closeChoice?.();
          closeChoice = null;
        },
        onOver(): void {
          // Render the final frame, then tear down and hand the run back.
          setTimeout(() => finish(false), 450);
        },
      },
    });
    quit.addEventListener('click', () => finish(true));
    loop.start();
  });
}
